import { COMMON_MASTER_GROUP_KEYS, isHubMasterGroup } from '../constants/commonMasterGroups';
import { getEmbeddedExternalCatalogGroup } from '../constants/commonExternalMasters';
import type { MasterCatalogGroup } from '../constants/masters';

const REMOVED_TYPES = new Set(['states', 'cities']);

function isValidCatalogType(type: { key?: string; label?: string }) {
  return Boolean(type.key?.trim() && type.label?.trim());
}

function sanitizeCatalogGroups(groups: MasterCatalogGroup[]): MasterCatalogGroup[] {
  return groups
    .map((group) => ({
      ...group,
      types: group.types.filter(isValidCatalogType),
    }))
    .filter((group) => group.types.length > 0);
}

function mergeCatalogGroup(existing: MasterCatalogGroup, incoming: MasterCatalogGroup): MasterCatalogGroup {
  const seen = new Set(existing.types.map((t) => t.key));
  const extraTypes = incoming.types.filter((t) => !seen.has(t.key));
  return {
    ...existing,
    label: existing.label?.trim() || incoming.label,
    types: [...existing.types, ...extraTypes],
  };
}

function appendCatalogGroup(catalog: MasterCatalogGroup[], group: MasterCatalogGroup): MasterCatalogGroup[] {
  const index = catalog.findIndex((g) => g.key === group.key);
  if (index < 0) return [...catalog, group];
  const next = [...catalog];
  next[index] = mergeCatalogGroup(next[index], group);
  return next;
}

/** Hidden from global Master Data sidebar (dedicated pages / unused for now). */
const HIDDEN_COMMON_MASTER_GROUPS = new Set([
  'app_masters',
  'app_promotions',
  '_external',
]);

/**
 * Groups with dedicated pages (Customer App → Home Banners / Promos & Coupons). The generic
 * master form skips their rules, so they are hidden from every master catalog, including service workspaces.
 */
const DEDICATED_PAGE_GROUPS = new Set(['app_masters', 'app_promotions']);

/** Master groups hidden from service workspace Masters submenu (entire group). */
const HIDDEN_MASTER_GROUPS_BY_SCOPE: Record<string, readonly string[]> = {
  'delivery:porter-service': ['delivery_masters', 'food_masters', 'grocery_masters'],
};

/**
 * Master types hidden from service workspace Masters submenu.
 * Zone pricing / earnings use /zones; transport attributes are not wired to runtime yet.
 */
const HIDDEN_MASTER_TYPES_BY_SCOPE: Record<string, readonly string[]> = {
  'transport:transport': [
    'ride_attributes',
    'ride_attribute_values',
    'ride_tags',
    'service_availability_schedules',
  ],
  'delivery:food-delivery': [
    'food_variants',
    'delivery_slots',
    'packaging_types',
    'delivery_partners_tiers',
    'substitution_rules',
    'cancellation_reasons',
    'delivery_partner_pricing',
    'delivery_incentives',
  ],
  'delivery:grocery-delivery': [
    'delivery_slots',
    'packaging_types',
    'delivery_partners_tiers',
    'substitution_rules',
    'cancellation_reasons',
    'delivery_partner_pricing',
    'delivery_incentives',
    'stock_alerts',
  ],
};
function scopeKey(serviceHub?: string, serviceSlug?: string): string | undefined {
  if (!serviceHub) return undefined;
  if (serviceSlug) return `${serviceHub}:${serviceSlug}`;
  return serviceHub;
}

function hiddenGroupsForScope(serviceHub?: string, serviceSlug?: string): Set<string> {
  const key = scopeKey(serviceHub, serviceSlug);
  if (!key) return new Set();
  return new Set(HIDDEN_MASTER_GROUPS_BY_SCOPE[key] ?? []);
}

function hiddenTypesForScope(serviceHub?: string, serviceSlug?: string): Set<string> {
  const key = scopeKey(serviceHub, serviceSlug);
  if (!key) return new Set();
  return new Set([
    ...REMOVED_TYPES,
    ...(HIDDEN_MASTER_TYPES_BY_SCOPE[key] ?? []),
  ]);
}

export function filterMasterCatalog(
  catalogRaw: MasterCatalogGroup[],
  opts: {
    embedded?: boolean;
    serviceHub?: string;
    serviceSlug?: string;
    enabledModules: string[];
    isSuperAdmin: boolean;
    restrictGroupKeys?: string[];
  },
): MasterCatalogGroup[] {
  const {
    embedded = false,
    serviceHub,
    serviceSlug,
    enabledModules,
    isSuperAdmin,
    restrictGroupKeys,
  } = opts;

  const hiddenGroups = hiddenGroupsForScope(serviceHub, serviceSlug);
  const hiddenTypes = hiddenTypesForScope(serviceHub, serviceSlug);
  const hideCommonGroups = !embedded && !restrictGroupKeys?.length;

  let merged = sanitizeCatalogGroups(
    catalogRaw
      .filter((group) => !hiddenGroups.has(group.key))
      .filter((group) => !(DEDICATED_PAGE_GROUPS.has(group.key) && !restrictGroupKeys?.includes(group.key)))
      .filter((group) => !(hideCommonGroups && HIDDEN_COMMON_MASTER_GROUPS.has(group.key)))
      .map((group) => ({
        ...group,
        types: group.types.filter((t) => !hiddenTypes.has(t.key)),
      })),
  );

  if (restrictGroupKeys?.length) {
    merged = merged.filter((group) => restrictGroupKeys.includes(group.key));
  }

  if (!restrictGroupKeys?.length) {
    const externalGroup = getEmbeddedExternalCatalogGroup({
      embedded,
      serviceHub,
      serviceSlug,
      enabledModules,
      isSuperAdmin,
    });
    // Keep service-hub embeds (e.g. Restaurant Master); hide Partners & Payout on global Master Data.
    if (externalGroup && (embedded || !HIDDEN_COMMON_MASTER_GROUPS.has(externalGroup.key))) {
      merged = appendCatalogGroup(merged, externalGroup);
    }
  }

  if (isSuperAdmin) return sanitizeCatalogGroups(merged);

  const hubScoped = (groupKey: string) => embedded && isHubMasterGroup(groupKey, serviceHub);
  merged = merged
    .map((group) => {
      if (COMMON_MASTER_GROUP_KEYS.has(group.key) || restrictGroupKeys?.includes(group.key) || hubScoped(group.key)) {
        return group;
      }
      return {
        ...group,
        types: group.types.filter((t) => !t.module || enabledModules.includes(t.module)),
      };
    })
    .filter((group) => {
      if (group.types.length === 0) return false;
      if (COMMON_MASTER_GROUP_KEYS.has(group.key) || restrictGroupKeys?.includes(group.key) || hubScoped(group.key)) return true;
      return !group.module || enabledModules.includes(group.module);
    });
  return sanitizeCatalogGroups(merged);
}

/** Visible transport master type keys for the service workspace (Masters submenu). */
export const TRANSPORT_VISIBLE_MASTER_TYPES = [
  'trip_types',
  'vehicle_types',
  'vehicle_brands',
  'vehicle_models',
  'vehicle_variants',
] as const;
